import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CloudOff, KeyRound, Lock } from 'lucide-react';
import { useDeviceVault } from '../../contexts/DeviceVaultContext';
import { MAX_PIN_ATTEMPTS, PIN_PATTERN } from '../../services/assessmentStore';

function Shell({ icon: Icon, title, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-700">
          <Icon className="h-5 w-5" />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function PinInput({ id, label, value, onChange, autoFocus }) {
  return (
    <label htmlFor={id} className="mt-4 block text-sm font-medium text-slate-700">
      {label}
      <input
        id={id}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        maxLength={12}
        autoFocus={autoFocus}
        className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-center text-lg tracking-[0.4em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      />
    </label>
  );
}

const primary = 'mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50';

// Shown after an online sign-in when this device has no offline PIN, or the
// PIN was locked out. The new PIN must be typed twice before the portal opens.
export function PinSetupScreen() {
  const vault = useDeviceVault();
  const [step, setStep] = useState('enter');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const lockedOut = vault.reason === 'locked-out';
  const optIn = vault.reason === 'opt-in';

  const skip = async () => {
    setBusy(true);
    try {
      await vault.skipOffline();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const next = (e) => {
    e.preventDefault();
    if (!PIN_PATTERN.test(pin)) {
      setError('Use 6 to 12 digits.');
      return;
    }
    setError(null);
    setStep('confirm');
  };

  const save = async (e) => {
    e.preventDefault();
    if (confirm !== pin) {
      setError("Those PINs didn't match. Start again.");
      setPin('');
      setConfirm('');
      setStep('enter');
      return;
    }
    setBusy(true);
    try {
      await vault.setPin(pin);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Shell icon={KeyRound} title={lockedOut ? 'Choose a new offline PIN' : 'Set up an offline PIN'}>
      <p className="mt-2 text-sm text-slate-600">
        {lockedOut
          ? `Your offline PIN was locked after ${MAX_PIN_ATTEMPTS} incorrect attempts. Choose a new one; it can be the same as before.`
          : 'This PIN opens your saved assessments on this device when you are offline. You will not need it while online.'}
      </p>
      <p className="mt-2 text-xs text-slate-500">
        You can skip this if you won't work offline on this device. You won't be asked again here, and you can
        turn it on later from the Assessments page.
      </p>
      {step === 'enter' ? (
        <form onSubmit={next}>
          <PinInput id="pin-new" label="New PIN (6–12 digits)" value={pin} onChange={setPin} autoFocus />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <button type="submit" className={primary} disabled={pin.length < 6}>Continue</button>
          <button type="button" onClick={skip} disabled={busy}
            className="mt-2 w-full rounded-lg px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            {optIn ? 'Not now' : "Skip � I won't work offline on this device"}
          </button>
        </form>
      ) : (
        <form onSubmit={save}>
          <PinInput id="pin-confirm" label="Enter the same PIN again" value={confirm} onChange={setConfirm} autoFocus />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <button type="submit" className={primary} disabled={confirm.length < 6 || busy}>
            {busy ? 'Saving…' : 'Save PIN'}
          </button>
          <button type="button" className="mt-2 w-full text-sm text-slate-500 hover:text-slate-700"
            onClick={() => { setStep('enter'); setConfirm(''); setError(null); }}>
            Back
          </button>
        </form>
      )}
    </Shell>
  );
}

const REASON_TEXT = {
  'locked-out': `This device was locked after ${MAX_PIN_ATTEMPTS} incorrect PINs. Sign in when you are back online to unlock it and choose a new PIN.`,
  expired: 'Offline access ends 7 days after your last online sign-in. Sign in when you are back online.',
  'no-pin': 'Offline access has not been set up on this device. Sign in online once to set a PIN.',
  disabled: 'Offline access is not enabled for your account. Ask an administrator, or sign in when you are back online.',
};

// PIN entry for offline access. `profiles` are the users who have a vault on
// this device; `fixedUserId` limits it to one (the signed-in user).
export function PinUnlockScreen({ fixedUserId, onUnlocked }) {
  const vault = useDeviceVault();
  const [profiles, setProfiles] = useState(null);
  const [userId, setUserId] = useState(fixedUserId ? String(fixedUserId) : null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    vault.offlineProfiles().then((list) => {
      const shown = fixedUserId ? list.filter((p) => p.userId === String(fixedUserId)) : list;
      setProfiles(shown);
      if (!userId && shown.length) setUserId(shown[0].userId);
    }).catch(() => setProfiles([]));
  }, [fixedUserId]); // eslint-disable-line

  const current = profiles?.find((p) => p.userId === userId);
  useEffect(() => {
    if (!current) return;
    if (current.lockedOut) setBlocked('locked-out');
    else if (!current.offlineEnabled) setBlocked('disabled');
    else if (!current.hasPin) setBlocked('no-pin');
    else if (!current.withinWindow) setBlocked('expired');
    else setBlocked(null);
  }, [current]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await vault.unlockWithPin(userId, pin, current.profile);
      onUnlocked?.();
    } catch (err) {
      setPin('');
      if (err.code === 'wrong-pin') {
        setError(`Incorrect PIN. ${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left.`);
      } else if (REASON_TEXT[err.code]) {
        setBlocked(err.code);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (profiles === null) return <Shell icon={Lock} title="Saved assessments"><p className="mt-2 text-sm text-slate-500">Loading…</p></Shell>;

  if (!profiles.length) {
    return (
      <Shell icon={CloudOff} title="No saved assessments here">
        <p className="mt-2 text-sm text-slate-600">
          This device has not been set up for offline use. Sign in while online to set an offline PIN.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium text-blue-700">Go to sign in</Link>
      </Shell>
    );
  }

  const name = (p) => [p.profile?.firstName, p.profile?.lastName].filter(Boolean).join(' ') || p.profile?.email || 'User';

  return (
    <Shell icon={Lock} title="Unlock saved assessments">
      <p className="mt-2 text-sm text-slate-600">You're offline. Enter your offline PIN to open the assessments saved on this device.</p>
      {profiles.length > 1 && !fixedUserId && (
        <label className="mt-4 block text-sm font-medium text-slate-700">
          Who's using this device?
          <select className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={userId || ''}
            onChange={(e) => { setUserId(e.target.value); setPin(''); setError(null); }}>
            {profiles.map((p) => <option key={p.userId} value={p.userId}>{name(p)}</option>)}
          </select>
        </label>
      )}
      {profiles.length === 1 && current && <p className="mt-3 text-sm font-medium text-slate-800">{name(current)}</p>}

      {blocked ? (
        <div className="mt-4 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{REASON_TEXT[blocked]}</span>
        </div>
      ) : (
        <form onSubmit={submit}>
          <PinInput id="pin-unlock" label="Offline PIN" value={pin} onChange={setPin} autoFocus />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <button type="submit" className={primary} disabled={pin.length < 6 || busy}>
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      )}
      <Link to="/" className="mt-4 inline-block text-sm text-slate-500 hover:text-slate-700">Sign in instead</Link>
    </Shell>
  );
}

// Public route: /offline
export function OfflineAccessPage() {
  const navigate = useNavigate();
  return <PinUnlockScreen onUnlocked={() => navigate('/dashboard/assessments', { replace: true })} />;
}

// Wraps the dashboard. Holds the portal back until this device's saved
// assessments are in the right state for the signed-in user.
export function DeviceGate({ children }) {
  const vault = useDeviceVault();
  if (!vault.applies) return children;
  switch (vault.status) {
    case 'idle':
    case 'checking':
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        </div>
      );
    case 'needs-pin':
      return <PinSetupScreen />;
    case 'locked':
      return <PinUnlockScreen fixedUserId={vault.userId} />;
    case 'unavailable':
      return (
        <Shell icon={CloudOff} title="You're offline">
          <p className="mt-2 text-sm text-slate-600">
            This device has not been set up for offline use yet. Connect to the internet and reload to continue.
          </p>
          <button type="button" className={primary} onClick={() => window.location.reload()}>Try again</button>
        </Shell>
      );
    default:
      return children;
  }
}
